import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ImageLightboxProps {
  images: Array<{ id: string; image_url: string; title?: string | null; description?: string | null }>;
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function ImageLightbox({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
}: ImageLightboxProps) {
  const currentImage = images[currentIndex];
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    onNavigate(newIndex);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    onNavigate(newIndex);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Swipe handlers for mobile
  const minSwipeDistance = 50;

  const handleSwipe = () => {
    if (!touchStartX.current || !touchEndX.current || isTransitioning) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && images.length > 1) {
      handleNext();
    }
    if (isRightSwipe && images.length > 1) {
      handlePrevious();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
      setIsTransitioning(false);
    };
  }, [isOpen, currentIndex, images.length, onNavigate, onClose]);

  if (!isOpen || !currentImage) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
      onClick={onClose}
    >
      {/* Close Button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-50 text-white hover:bg-white/20 hover:text-white h-10 w-10 md:h-12 md:w-12"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close lightbox"
      >
        <X className="h-5 w-5 md:h-6 md:w-6" />
      </Button>

      {/* Main Content Container */}
      <div className="flex items-center justify-center w-full h-full p-4">
        {/* Previous Button */}
        {images.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 md:left-4 z-40 text-white hover:bg-white/20 hover:text-white h-10 w-10 md:h-12 md:w-12 top-1/2 -translate-y-1/2"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            aria-label="Previous image"
          >
            <ChevronLeft className="h-5 w-5 md:h-8 md:w-8" />
          </Button>
        )}

        {/* Image Container */}
        <div
          className="relative flex flex-col items-center justify-center max-w-4xl max-h-full"
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => {
            e.stopPropagation();
            touchEndX.current = null;
            touchStartX.current = e.targetTouches[0].clientX;
          }}
          onTouchMove={(e) => {
            e.stopPropagation();
            touchEndX.current = e.targetTouches[0].clientX;
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            handleSwipe();
          }}
        >
          <img
            ref={imageRef}
            src={currentImage.image_url}
            alt={currentImage.title || "Gallery image"}
            className={`max-w-full max-h-[70vh] md:max-h-[80vh] object-contain rounded-lg select-none transition-transform duration-300 ${
              isTransitioning ? "scale-95 opacity-80" : "scale-100 opacity-100"
            }`}
            loading="eager"
            draggable={false}
          />
          
          {/* Image Info */}
          {(currentImage.title || currentImage.description) && (
            <div className="mt-3 md:mt-4 text-center max-w-2xl px-4">
              {currentImage.title && (
                <h3 className="text-lg md:text-2xl font-bold text-white mb-1 md:mb-2 font-poppins line-clamp-2">
                  {currentImage.title}
                </h3>
              )}
              {currentImage.description && (
                <p className="text-xs md:text-base text-white/80 font-inter line-clamp-3">
                  {currentImage.description}
                </p>
              )}
            </div>
          )}

          {/* Image Counter */}
          {images.length > 1 && (
            <div className="mt-3 md:mt-4 text-white/80 text-sm font-inter bg-black/40 px-3 py-1 rounded-full">
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {/* Mobile Navigation Dots */}
          {images.length > 1 && (
            <div className="flex space-x-2 mt-4 md:hidden">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex ? "bg-white scale-110" : "bg-white/40"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (index !== currentIndex) {
                      setIsTransitioning(true);
                      onNavigate(index);
                      setTimeout(() => setIsTransitioning(false), 300);
                    }
                  }}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Next Button */}
        {images.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 md:right-4 z-40 text-white hover:bg-white/20 hover:text-white h-10 w-10 md:h-12 md:w-12 top-1/2 -translate-y-1/2"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            aria-label="Next image"
          >
            <ChevronRight className="h-5 w-5 md:h-8 md:w-8" />
          </Button>
        )}
      </div>
    </div>
  );
}